// Per-project Document_Type, Output_Language, and LLM model settings.
// On persistence failure the UI keeps the selection and lets the user continue
// (R11.4 uses default study-material for generation; R12.4 keeps chosen language).

import React from "react";
import type { DocumentType, OutputLanguage } from "@asmg/shared";
import { DOCUMENT_TYPES, OUTPUT_LANGUAGES } from "@asmg/shared";
import { api } from "../api/client";

const LANG_LABELS: Record<OutputLanguage, string> = {
  hinglish: "Hinglish",
  english: "English",
  hindi: "Hindi",
};

// Shown at the top of the model list for convenience (verified fast).
const RECOMMENDED = ["meta/llama-3.1-8b-instruct", "google/gemma-2-2b-it"];

export function SettingsPanel({
  projectId,
  documentType,
  language,
  llmModel,
  onUpdated,
}: {
  projectId: string;
  documentType: DocumentType;
  language: OutputLanguage;
  llmModel?: string;
  onUpdated: (patch: { documentType?: DocumentType; language?: OutputLanguage; llmModel?: string }) => void;
}) {
  const [dt, setDt] = React.useState(documentType);
  const [lang, setLang] = React.useState(language);
  const [model, setModel] = React.useState(llmModel ?? "");
  const [models, setModels] = React.useState<string[]>([]);
  const [warn, setWarn] = React.useState<string | null>(null);

  React.useEffect(() => {
    setDt(documentType);
    setLang(language);
    setModel(llmModel ?? "");
  }, [documentType, language, llmModel]);

  React.useEffect(() => {
    api
      .listModels()
      .then((r) => {
        const all = r.models.filter((m) => /instruct|-it$|gemma|deepseek|nemotron|gpt-oss|qwen|mistral|llama/i.test(m));
        const ordered = [...RECOMMENDED.filter((m) => all.includes(m)), ...all.filter((m) => !RECOMMENDED.includes(m)).sort()];
        setModels(ordered);
      })
      .catch(() => setModels([]));
  }, []);

  async function persist(patch: { documentType?: DocumentType; language?: OutputLanguage; llmModel?: string }) {
    onUpdated(patch); // keep UI selection regardless of persistence outcome
    setWarn(null);
    try {
      await api.updateSettings(projectId, patch);
    } catch {
      setWarn("Couldn't save the setting — continuing with your selection.");
    }
  }

  return (
    <div className="row" style={{ gap: 16, flexWrap: "wrap" }}>
      <div>
        <label htmlFor="s-doctype" style={{ margin: 0 }}>Type</label>
        <select id="s-doctype" value={dt} onChange={(e) => { const v = e.target.value as DocumentType; setDt(v); void persist({ documentType: v }); }}>
          {DOCUMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>
      <div>
        <label htmlFor="s-lang" style={{ margin: 0 }}>Language</label>
        <select id="s-lang" value={lang} onChange={(e) => { const v = e.target.value as OutputLanguage; setLang(v); void persist({ language: v }); }}>
          {OUTPUT_LANGUAGES.map((l) => <option key={l} value={l}>{LANG_LABELS[l]}</option>)}
        </select>
      </div>
      <div style={{ minWidth: 240 }}>
        <label htmlFor="s-model" style={{ margin: 0 }}>Model</label>
        <select id="s-model" value={model} onChange={(e) => { const v = e.target.value; setModel(v); void persist({ llmModel: v }); }}>
          <option value="">Default (fast: llama-3.1-8b)</option>
          {models.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>
      {warn && <span className="muted" style={{ color: "#b9770e", flexBasis: "100%" }}>{warn}</span>}
    </div>
  );
}
